import { useEffect, useMemo, useState } from "react";
import {
  type ConversationAction,
  useForwardAction,
  useReplyAction,
  useReplyAllAction,
} from "../actions/use-conversation-actions";

export const replyModes = ["reply", "replyAll", "forward"] as const;
export type ReplyMode = (typeof replyModes)[number];

export interface UseReplyModeResult {
  replyMode: ReplyMode;
  setReplyMode: (mode: ReplyMode) => void;
  activeReplyAction: ConversationAction;
  replyModeActions: Record<ReplyMode, ConversationAction>;
}

export function useReplyMode(
  initialMode: ReplyMode = "reply",
): UseReplyModeResult {
  const [replyMode, setReplyMode] = useState<ReplyMode>(initialMode);

  useEffect(() => {
    setReplyMode(initialMode);
  }, [initialMode]);

  const replyAction = useReplyAction(() => setReplyMode("reply"));
  const replyAllAction = useReplyAllAction(() => setReplyMode("replyAll"));
  const forwardAction = useForwardAction(() => setReplyMode("forward"));

  const replyModeActions: Record<ReplyMode, ConversationAction> = useMemo(
    () => ({
      reply: replyAction,
      replyAll: replyAllAction,
      forward: forwardAction,
    }),
    [replyAction, replyAllAction, forwardAction],
  );

  const activeReplyAction = replyModeActions[replyMode];

  return { replyMode, setReplyMode, activeReplyAction, replyModeActions };
}
